/**
 * 四子棋游戏逻辑
 * 8x8棋盘，垂直落子，四子连线获胜
 */

import { Player, Position, GameStatus, GameMode, Difficulty, ConnectFourGameState, ConnectFourMove } from '../types/game';

// 四子棋游戏状态

// 创建初始游戏状态
export const createInitialConnectFourState = (
  gameMode: GameMode,
  difficulty: Difficulty = 'medium'
): ConnectFourGameState => {
  const board: (Player | null)[][] = [];
  for (let row = 0; row < 8; row++) {
    board[row] = [];
    for (let col = 0; col < 8; col++) {
      board[row][col] = null;
    }
  }

  return {
    board,
    currentPlayer: 'black',
    status: 'playing',
    winner: null,
    moves: [],
    gameHistory: [],
    gameMode,
    gameType: 'connectfour',
    difficulty,
    isAIThinking: false,
    lastMove: null
  };
};

// 获取列的最低空位
export const getLowestEmptyRow = (board: (Player | null)[][], col: number): number | null => {
  for (let row = 7; row >= 0; row--) {
    if (board[row][col] === null) {
      return row;
    }
  }
  return null; // 列已满
};

// 检查列是否已满
export const isColumnFull = (board: (Player | null)[][], col: number): boolean => {
  return getLowestEmptyRow(board, col) === null;
};

// 检查棋盘是否已满
export const isBoardFull = (board: (Player | null)[][]): boolean => {
  for (let col = 0; col < 8; col++) {
    if (!isColumnFull(board, col)) {
      return false;
    }
  }
  return true;
};

// 检查是否有玩家获胜（四子连线）
export const checkWinner = (board: (Player | null)[][], lastMove: Position): Player | null => {
  const { row, col } = lastMove;
  const player = board[row][col];

  if (!player) return null;

  // 检查四个方向：水平、垂直、两个对角线
  const directions = [
    { dr: 0, dc: 1 },  // 水平
    { dr: 1, dc: 0 },  // 垂直
    { dr: 1, dc: 1 },  // 右下对角线
    { dr: 1, dc: -1 }  // 左下对角线
  ];

  for (const { dr, dc } of directions) {
    let count = 1; // 包括当前棋子

    // 向正方向检查
    let r = row + dr;
    let c = col + dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
      count++;
      r += dr;
      c += dc;
    }

    // 向负方向检查
    r = row - dr;
    c = col - dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
      count++;
      r -= dr;
      c -= dc;
    }

    if (count >= 4) {
      return player;
    }
  }

  return null;
};

// 执行移动
export const makeConnectFourMove = (
  gameState: ConnectFourGameState,
  col: number
): ConnectFourGameState => {
  const { board, currentPlayer, moves } = gameState;

  // 检查列是否有效
  if (col < 0 || col >= 8 || isColumnFull(board, col)) {
    return gameState;
  }

  // 获取落子位置
  const row = getLowestEmptyRow(board, col);
  if (row === null) return gameState;

  // 创建新棋盘
  const newBoard = board.map(row => [...row]);
  newBoard[row][col] = currentPlayer;

  // 创建移动记录
  const move: ConnectFourMove = {
    position: { row, col },
    player: currentPlayer,
    timestamp: Date.now()
  };

  // 检查获胜
  const winner = checkWinner(newBoard, move.position);

  // 检查游戏结束
  const isBoardFullCheck = isBoardFull(newBoard);
  const status = winner ? 'finished' : (isBoardFullCheck ? 'finished' : 'playing');

  const newGameState: ConnectFourGameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: currentPlayer === 'black' ? 'white' : 'black',
    status,
    winner,
    moves: [...moves, move],
    gameHistory: [...gameState.gameHistory, { ...gameState }],
    lastMove: move.position
  };

  return newGameState;
};

// 悔棋
export const undoConnectFourMove = (gameState: ConnectFourGameState): ConnectFourGameState => {
  if (gameState.moves.length === 0) return gameState;

  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
  return previousState;
};

// 重置游戏
export const resetConnectFourGame = (
  gameMode: GameMode,
  difficulty?: Difficulty
): ConnectFourGameState => {
  return createInitialConnectFourState(gameMode, difficulty);
};

// 获取所有可落子的列
export const getAvailableColumns = (board: (Player | null)[][]): number[] => {
  const available: number[] = [];
  for (let col = 0; col < 8; col++) {
    if (!isColumnFull(board, col)) {
      available.push(col);
    }
  }
  return available;
};

// 检查游戏是否结束
export const isGameOver = (gameState: ConnectFourGameState): boolean => {
  return gameState.status === 'finished';
};

// 获取游戏状态文本
export const getConnectFourStatusText = (gameState: ConnectFourGameState): string => {
  if (gameState.status === 'finished') {
    if (gameState.winner) {
      return `${gameState.winner === 'black' ? '黑方' : '白方'}获胜！`;
    } else {
      return '平局！';
    }
  }
  return `${gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合`;
};

// 获取棋盘大小文本
export const getConnectFourBoardSizeText = (): string => {
  return '8×8';
};

// 评估当前棋盘局势（用于AI）
export const evaluateBoard = (board: (Player | null)[][], player: Player): number => {
  let score = 0;

  // 检查每个位置
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      if (board[row][col] === player) {
        score += evaluatePosition(board, row, col, player);
      } else if (board[row][col] === (player === 'black' ? 'white' : 'black')) {
        score -= evaluatePosition(board, row, col, player === 'black' ? 'white' : 'black');
      }
    }
  }

  return score;
};

// 评估位置的重要性
const evaluatePosition = (board: (Player | null)[][], row: number, col: number, player: Player): number => {
  let score = 0;

  // 中心列更有价值
  const centerDistance = Math.abs(3.5 - col);
  score += (4 - centerDistance) * 10;

  // 检查是否能形成连线
  score += evaluateConnections(board, row, col, player) * 100;

  // 底部位置更有优势（因为可以向上发展）
  score += (7 - row) * 5;

  return score;
};

// 评估连线潜力
const evaluateConnections = (board: (Player | null)[][], row: number, col: number, player: Player): number => {
  let maxConnections = 0;

  const directions = [
    { dr: 0, dc: 1 },  // 水平
    { dr: 1, dc: 0 },  // 垂直
    { dr: 1, dc: 1 },  // 右下对角线
    { dr: 1, dc: -1 }  // 左下对角线
  ];

  for (const { dr, dc } of directions) {
    let count = 1;

    // 向正方向计数
    let r = row + dr;
    let c = col + dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
      count++;
      r += dr;
      c += dc;
    }

    // 向负方向计数
    r = row - dr;
    c = col - dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
      count++;
      r -= dr;
      c -= dc;
    }

    maxConnections = Math.max(maxConnections, count);
  }

  return maxConnections >= 3 ? maxConnections * maxConnections : 0;
};

// 检查是否需要防守对手的2子或3子连线
const checkDefensiveMove = (
  board: (Player | null)[][],
  opponent: Player
): { col: number; priority: number } | null => {
  const availableCols = getAvailableColumns(board);
  let bestDefense: { col: number; priority: number } | null = null;

  for (const col of availableCols) {
    const row = getLowestEmptyRow(board, col);
    if (row !== null) {
      const testBoard = board.map(r => [...r]);
      testBoard[row][col] = opponent;

      // 检查对手在此位置能形成多强的连线
      const threatLevel = evaluateThreatLevel(testBoard, row, col, opponent);

      // 如果威胁等级高，需要防守
      if (threatLevel >= 2) {
        // 3子连线（威胁等级3）优先级最高，2子连线（威胁等级2）优先级次之
        if (!bestDefense || threatLevel > bestDefense.priority) {
          bestDefense = { col, priority: threatLevel };
        }
      }
    }
  }

  return bestDefense;
};

// 评估威胁等级
const evaluateThreatLevel = (
  board: (Player | null)[][],
  row: number,
  col: number,
  player: Player
): number => {
  let maxThreat = 0;

  const directions = [
    { dr: 0, dc: 1 },  // 水平
    { dr: 1, dc: 0 },  // 垂直
    { dr: 1, dc: 1 },  // 右下对角线
    { dr: 1, dc: -1 }  // 左下对角线
  ];

  for (const { dr, dc } of directions) {
    let count = 1;
    let openEnds = 0;

    // 向正方向计数
    let r = row + dr;
    let c = col + dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
      count++;
      r += dr;
      c += dc;
    }
    // 检查正方向是否有空位
    if (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === null) {
      openEnds++;
    }

    // 向负方向计数
    r = row - dr;
    c = col - dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === player) {
      count++;
      r -= dr;
      c -= dc;
    }
    // 检查负方向是否有空位
    if (r >= 0 && r < 8 && c >= 0 && c < 8 && board[r][c] === null) {
      openEnds++;
    }

    // 计算威胁等级：考虑连子数和开放端
    if (count >= 2 && openEnds > 0) {
      maxThreat = Math.max(maxThreat, count);
    }
  }

  return maxThreat;
};

// 获取AI决策
export const getConnectFourAIDecision = (
  gameState: ConnectFourGameState,
  difficulty: Difficulty
): { col: number } | null => {
  const { board, currentPlayer } = gameState;
  const availableCols = getAvailableColumns(board);

  if (availableCols.length === 0) return null;

  // 简单难度：随机选择
  if (difficulty === 'easy') {
    const randomCol = availableCols[Math.floor(Math.random() * availableCols.length)];
    return { col: randomCol };
  }

  // 中等和困难难度：优先检查获胜和防守
  if (difficulty === 'medium' || difficulty === 'hard') {
    // 1. 首先检查自己是否能直接获胜
    for (const col of availableCols) {
      const row = getLowestEmptyRow(board, col);
      if (row !== null) {
        const testBoard = board.map(r => [...r]);
        testBoard[row][col] = currentPlayer;

        const winner = checkWinner(testBoard, { row, col });
        if (winner === currentPlayer) {
          return { col }; // 直接获胜
        }
      }
    }

    // 2. 检查是否需要防守对手的威胁（2子或3子连线）
    const opponent = currentPlayer === 'black' ? 'white' : 'black';
    const defenseMove = checkDefensiveMove(board, opponent);

    if (defenseMove && (difficulty === 'hard' || defenseMove.priority >= 3)) {
      // 困难模式：防守2子和3子连线
      // 中等模式：只防守3子连线（直接威胁）
      return { col: defenseMove.col };
    }
  }

  // 3. 如果没有直接威胁，使用评估函数选择最佳位置
  let bestCol = availableCols[0];
  let bestScore = -Infinity;

  for (const col of availableCols) {
    // 模拟落子
    const row = getLowestEmptyRow(board, col);
    if (row !== null) {
      const testBoard = board.map(r => [...r]);
      testBoard[row][col] = currentPlayer;

      // 评估这个位置
      const score = evaluateBoard(testBoard, currentPlayer);
      if (score > bestScore) {
        bestScore = score;
        bestCol = col;
      }
    }
  }

  return { col: bestCol };
};